import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  Alert,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import type { FormSubmissionDetail, FormTemplateSummary } from '@isg/shared-types';
import type { FormSema } from '@isg/validation';
import { formKapSemasi } from '@isg/validation';
import {
  useFormDoldurmaHedefleri,
  useFormEkIndir,
  useFormEkSil,
  useFormEkYukle,
  useFormGonder,
  useFormKayit,
  useFormKayitAc,
  useFormKayitGuncelle,
  useFormSablon,
  useFormSurum,
  useGorev,
  useLookup,
  useSahaTuru,
} from '../api/queries';
import {
  FormAlanlari,
  digerEksigiMetni,
  digerEksikleri,
  digerMetinleriniOku,
  gonderilecekDigerMetinleri,
  semayiCoz,
} from './form-parts';
import type { CevapHaritasi, DigerMetinleri } from './form-parts';
import { QueryStatus } from './parts';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import { ANLAM } from '../theme';
import { useVarsayilanGun } from '../lib/saha-gunu';

/**
 * FORM DOLDURMA SAYFASI (Tur 3 — F6 + F7).
 *
 * ⚠️ **MADDE 122 — TASLAK SUNUCUDA ERKEN AÇILIR.** Yarım form verisi bugüne
 * kadar YALNIZ React durumundaydı: kullanıcı sayfayı yeniler ya da oturumu
 * kopar da yeniden girerse **yazdığı her şey kayboluyordu.** *Sahadaki
 * kullanıcı formu yarım doldurur, telefona bakar, kaydete basar; verisini
 * kaybederse o formu bir daha doldurmaz.*
 *
 * **ÇÖZÜM YENİ MEKANİZMA İSTEMEDİ** (ürün sahibi seçeneği b): §22.5/3 zaten
 * *"önce aç → doldur → gönder"* sırasını destekliyordu. İşyeri seçilir
 * seçilmez kayıt **TASLAK olarak sunucuda açılır**; sonraki her değişiklik
 * ~1.2 sn sessizlikten sonra `PATCH` ile yazılır.
 *
 * ⚠️ **TARAYICI DEPOSU KULLANILMAZ** (seçenek a bilinçli REDDEDİLDİ):
 * kişisel veri tarayıcıda kalır ve KVKK yüzeyi + temizleme kuralı ister.
 *
 * ⚠️ **OTOMATİK KAYIT TETİĞİ ZAMAN DEĞİL, DEĞİŞİKLİKTİR** ve gecikmelidir:
 * her tuşta istek atmak checklist'te onlarca isteğe çıkardı; sabit periyotlu
 * bir zamanlayıcı ise hiçbir şey değişmemişken de yazardı. 1200 ms, bir
 * maddeden diğerine geçme süresinin üstünde ve kullanıcı "kaydedildi"
 * rozetini görmeden sayfadan çıkmayacak kadar kısadır.
 */
const OTOMATIK_KAYIT_GECIKMESI_MS = 1200;

export function FormDoldurScreen(): React.JSX.Element {
  const { sablonId } = useParams<{ sablonId: string }>();
  // SAHA TURU / S2 (Kapsam §27) — KAP: `?sahaTuru=<id>` ile açılan doldurma,
  // kaydı denetim oturumuna BAĞLAR (`siteTourId`). Kap yoksa kayıt olağan
  // form kaydıdır ve hiçbir bulgu doğurmaz (U10). İşyeri turdan gelir,
  // seçtirilmez; gönderim sonrası dönüş TURA.
  const [arama] = useSearchParams();
  const sahaTuruId = arama.get('sahaTuru') ?? null;
  // T3 (türetme paketi — bulgu 93): `?gorev=<occurrenceId>` ile açılan doldurma
  // GÖREVDEN türer — işyeri ve ekipman görevin nesnesidir, sorulmaz.
  const gorevId = sahaTuruId ? null : (arama.get('gorev') ?? null);
  // ✏️ F4·S6 (Y1): liste SAYFALANDI — "listeden bul" deseni artık yanlış
  // cevap verir (aranan şablon ikinci sayfadaysa "yok" der). TEKİL UÇ.
  const sablonlar = useFormSablon(sablonId ?? null);
  const sablon = sablonlar.data ?? null;

  return (
    <Stack data-testid="form-doldur-sayfasi">
      <Breadcrumbs data-testid="form-doldur-gezinti">
        {sahaTuruId ? (
          <Link to={`/saha-turlari/${sahaTuruId}`}>Saha turu</Link>
        ) : gorevId ? (
          <Link to={`/ekipman/gorevler/${gorevId}`}>Görev</Link>
        ) : (
          <Link to="/formlar">Formlar</Link>
        )}
        <Text>{sablon?.ad ?? 'Form doldur'}</Text>
      </Breadcrumbs>
      <QueryStatus queries={[sablonlar]} />
      {sablonlar.isSuccess &&
        (sablon === null ? (
          <Alert color={ANLAM.engel} role="alert" data-testid="form-doldur-bulunamadi">
            Form şablonu bulunamadı ya da görünürlüğünüzün dışında.
          </Alert>
        ) : (
          <DoldurmaGovdesi sablon={sablon} sahaTuruId={sahaTuruId} gorevId={gorevId} />
        ))}
    </Stack>
  );
}

/**
 * KAP İÇİNDE DOLDURMA (gezinti artçısı 07.09.2026 — G2.1 / 0b).
 *
 * Denetim formu **tur sayfasının içinde** doldurulur: `sablonId` + `sahaTuruId`
 * ile bu gövde bir bölümün içinde çizilir, sayfa değişmez. ⚠️ **İKİNCİ
 * DOLDURMA GÖVDESİ YAZILMADI** — aynı `DoldurmaGovdesi`, aynı uçlar
 * (`POST/PATCH form-submissions` + `siteTourId`); PTW tür formunun (S8) deseni.
 * `mevcutKayitId` verilirse taslak sunucudan yüklenir ve **ikinci taslak
 * açılmaz** (tur başına tek gönderim — kısmi unique 409 verirdi).
 */
export function KapIcindeFormDoldurma({
  sablonId,
  sahaTuruId,
  mevcutKayitId,
  onGonderildi,
  onVazgec,
}: {
  sablonId: string;
  sahaTuruId: string;
  mevcutKayitId: string | null;
  onGonderildi: () => void;
  /** T4 — gömülü denetim formu doğrudan görünür; kapatma düğmesi yoksa verilmez. */
  onVazgec?: () => void;
}): React.JSX.Element {
  // ✏️ F4·S6 (Y1): liste SAYFALANDI — "listeden bul" deseni artık yanlış
  // cevap verir (aranan şablon ikinci sayfadaysa "yok" der). TEKİL UÇ.
  const sablonlar = useFormSablon(sablonId ?? null);
  const sablon = sablonlar.data ?? null;
  return (
    <Stack data-testid="kap-icinde-form-doldurma">
      <QueryStatus queries={[sablonlar]} />
      {sablonlar.isSuccess &&
        (sablon === null ? (
          <Alert color={ANLAM.engel} role="alert" data-testid="kap-icinde-form-bulunamadi">
            Form şablonu bulunamadı ya da görünürlüğünüzün dışında.
          </Alert>
        ) : (
          <DoldurmaGovdesi
            sablon={sablon}
            sahaTuruId={sahaTuruId}
            mevcutKayitId={mevcutKayitId}
            kap={{ onGonderildi, onVazgec }}
          />
        ))}
    </Stack>
  );
}

function DoldurmaGovdesi({
  sablon,
  sahaTuruId,
  mevcutKayitId = null,
  kap = null,
  gorevId = null,
}: {
  sablon: FormTemplateSummary;
  sahaTuruId: string | null;
  /** T3 — görevden açıldıysa işyeri/ekipman görevden gelir (sunucu türetir). */
  gorevId?: string | null;
  /** Sunucuda zaten açık taslak — kap içinde devam (ikinci taslak açılmaz). */
  mevcutKayitId?: string | null;
  /** Kap içindeyse: gönderim sayfa değiştirmez, kap haber alır. */
  kap?: { onGonderildi: () => void; onVazgec?: () => void } | null;
}): React.JSX.Element {
  const navigate = useNavigate();
  const sahaTuru = useSahaTuru(sahaTuruId ?? undefined);
  const gorev = useGorev(gorevId ?? '', { enabled: gorevId !== null });
  const mevcutKayit = useFormKayit(mevcutKayitId);
  const surum = useFormSurum(sablon.yayinliSurumId);
  // ⚠️ HAVUZ SUNUCUDAN (F1): bahçe VE atama birlikte. Ekran şirket/işyeri
  // listesini kendi çıkarsaydı şapkasız çalışan boş liste görürdü.
  const hedefler = useFormDoldurmaHedefleri(sablon.id);
  const [workplaceId, setWorkplaceId] = useState<string | null>(null);
  const [ekipmanId, setEkipmanId] = useState<string | null>(null);
  const isyerleri = hedefler.data?.isyerleri ?? [];
  const secilenIsyeri = isyerleri.find((w) => w.workplaceId === workplaceId) ?? null;
  const isyeriSecenekleri = useMemo(
    () =>
      isyerleri.map((w) => ({ value: w.workplaceId, label: `${w.companyAd} — ${w.workplaceAd}` })),
    // Seçenek dizisi HER RENDER'DA yeniden kurulursa Mantine açılır listesi
    // kendini kapatır (e2e'de yakalandı) — kimlik listeye göre sabitlenir.
    [isyerleri.map((w) => w.workplaceId).join(',')],
  );
  const ekipmanSecenekleri = useMemo(
    () => (secilenIsyeri?.ekipmanlar ?? []).map((e) => ({ value: e.id, label: e.ad })),
    [secilenIsyeri?.ekipmanlar.map((e) => e.id).join(',')],
  );
  const lookup = useLookup('olay_turu');
  // Madde 302: varsayılan "bugün" SAHA GÜNÜ (sunucudan) — kullanıcı değiştirir;
  // mevcut taslak yüklenince kendi tarihi yazılır (dokunulmuş sayılır).
  const [tarih, setTarih] = useVarsayilanGun();
  const [cevaplar, setCevaplar] = useState<CevapHaritasi>({});
  // G1 — madde notları cevaplarla AYNI isteğe biner (tek yazma yolu).
  const [notlar, setNotlar] = useState<Record<string, string>>({});
  // K-D1 — "Diğer" metinleri de AYNI isteğe biner (notlarla aynı yol).
  const [digerMetinleri, setDigerMetinleri] = useState<DigerMetinleri>({});

  const kayitAc = useFormKayitAc();
  const kayitGuncelle = useFormKayitGuncelle();
  const gonder = useFormGonder();
  const ekYukle = useFormEkYukle();
  const ekSil = useFormEkSil();
  const ekIndir = useFormEkIndir();
  const [taslak, setTaslak] = useState<FormSubmissionDetail | null>(null);
  // ⚠️ **ROZET ÜÇ HÂL BİLİR, İKİ DEĞİL.** İki hâlli bir rozet ("kaydediliyor"
  // / "kayıtlı") gecikme penceresinde YALAN SÖYLER: kullanıcı yazmıştır,
  // istek daha atılmamıştır ve rozet "kayıtlı" der (§2.40). Üçüncü hâl
  // (`bekleyen`) tam olarak o pencereyi anlatır.
  const [bekleyen, setBekleyen] = useState(false);
  // Madde 221 — bekleyen otomatik kayıt varken sayfadan çıkış YÖNLENDİRİCİDE
  // uyarılır: zamanlayıcı sayfa kalkınca temizlenir ve yazılan kaybolurdu.
  // İstek zaten atılmışsa (isPending) korunacak bir şey yoktur — mutasyon
  // sayfa değişse de tamamlanır.
  useKaydedilmemisIcerik(bekleyen ? 1 : 0, 'kaydedilmemiş cevap');

  // T4 — kap: tur içinde üst kaydın bildiği alan sorulmaz (sunucuyla AYNI dönüşüm).
  const sema = useMemo<FormSema | null>(() => {
    const cozulen = semayiCoz(surum.data?.sema);
    return cozulen ? formKapSemasi(cozulen, { siteTourId: sahaTuruId }) : null;
  }, [surum.data, sahaTuruId]);

  // KAP (S2): tur verilmişse işyeri TURDAN gelir — kullanıcıya seçtirilmez
  // (turun işyeri dışında bir kayıt ucun 422'sine çarpardı — §2.40).
  useEffect(() => {
    if (mevcutKayitId) return; // işyeri taslaktan gelir
    if (sahaTuruId && sahaTuru.data && workplaceId === null) {
      setWorkplaceId(sahaTuru.data.workplaceId);
    }
    // T3 — görev nesnesinin işyeri; ekipman taslak yanıtından gelir.
    if (gorevId && gorev.data && workplaceId === null) {
      setWorkplaceId(gorev.data.nesne.workplaceId);
    }
  }, [sahaTuruId, sahaTuru.data, workplaceId, mevcutKayitId, gorevId, gorev.data]);

  // KAP İÇİNDE MEVCUT TASLAK: sunucudaki kayıt bir kez yerel duruma alınır
  // (cevap · not · tarih · ekipman); sonrası olağan otomatik kayıt yolu.
  useEffect(() => {
    if (!mevcutKayitId || taslak !== null || !mevcutKayit.isSuccess) return;
    const k = mevcutKayit.data;
    if (k.durum !== 'draft') return;
    setTaslak(k);
    setWorkplaceId(k.workplaceId);
    setEkipmanId(k.ekipmanId);
    setTarih(k.doldurmaTarihi);
    setCevaplar(Object.fromEntries(k.cevaplar.map((c) => [c.alanKodu, c.deger])));
    setNotlar(
      Object.fromEntries(
        k.cevaplar.filter((c) => c.notMetni !== null).map((c) => [c.alanKodu, c.notMetni ?? '']),
      ),
    );
    setDigerMetinleri(digerMetinleriniOku(k.cevaplar));
  }, [mevcutKayitId, mevcutKayit.isSuccess, mevcutKayit.data, taslak]);

  // ── F6 — TASLAK SUNUCUDA AÇILIR ────────────────────────────────────────
  // İşyeri seçilir seçilmez kayıt doğar; buradan sonra yazılan hiçbir şey
  // yalnız tarayıcıda kalmaz.
  useEffect(() => {
    if (!workplaceId || taslak !== null || kayitAc.isPending) return;
    // Mevcut taslak yüklenirken YENİ taslak açılmaz (kap başına tek kayıt).
    if (mevcutKayitId) return;
    kayitAc.mutate(
      {
        formTemplateId: sablon.id,
        // T3 — görevden: işyeri/ekipman GÖNDERİLMEZ, sunucu görevden türetir.
        ...(gorevId ? { taskOccurrenceId: gorevId } : { workplaceId }),
        doldurmaTarihi: tarih,
        cevaplar: {},
        notlar: {},
        digerMetinleri: {},
        taslak: true,
        // P13 · B1/3 — kap içi taslak TURUN LOKASYONUYLA açılır: tur detayının
        // `formKapisi` bayrağı form motorunun kapısını aynı hedefle değerlendirir
        // (lokasyonsuz açılış dal kapsamlı sorumluyu 403'e düşürüyordu).
        ...(sahaTuruId
          ? { siteTourId: sahaTuruId, locationNodeId: sahaTuru.data?.locationNodeId ?? null }
          : {}),
      },
      {
        onSuccess: (kayit) => {
          setTaslak(kayit);
          // T3 — ekipman yalnız GÖREVDEN açılan kayıtta yanıttan okunur (sunucu türetti).
          // ⚠️ Bağımsız formda kullanıcı ekipmanı taslak dönmeden seçmiş olabilir —
          // yanıtın boş ekipmanı o seçimi ezer ve gönderim görevi kapatmaz (e2e buldu).
          if (gorevId) setEkipmanId(kayit.ekipmanId);
        },
      },
    );
  }, [
    gorevId,
    workplaceId,
    taslak,
    kayitAc,
    sablon.id,
    tarih,
    sahaTuruId,
    mevcutKayitId,
    sahaTuru.data?.locationNodeId,
  ]);

  // Gecikmeli otomatik kayıt — değişiklik tetikler, zaman değil.
  const zamanlayici = useRef<ReturnType<typeof setTimeout> | null>(null);
  const yaz = (
    yeniCevaplar: CevapHaritasi,
    yeniTarih = tarih,
    yeniEkipman = ekipmanId,
    yeniNotlar = notlar,
    yeniDiger = digerMetinleri,
  ): void => {
    if (!taslak) return;
    if (zamanlayici.current) clearTimeout(zamanlayici.current);
    setBekleyen(true);
    // K-D1 — "Diğer" seçilip açıklaması yazılmamışken otomatik kayıt ATILMAZ:
    // sunucu yarım cevabı reddeder ve kullanıcı yazarken hata bandı görürdü.
    // Rozet "Kaydedilmedi"de kalır; açıklama yazılınca kayıt kendiliğinden gider.
    if (sema && digerEksikleri(sema, yeniCevaplar, yeniDiger).length > 0) return;
    zamanlayici.current = setTimeout(() => {
      kayitGuncelle.mutate({
        id: taslak.id,
        girdi: {
          cevaplar: yeniCevaplar,
          doldurmaTarihi: yeniTarih,
          ekipmanId: yeniEkipman,
          notlar: yeniNotlar,
          digerMetinleri: sema ? gonderilecekDigerMetinleri(sema, yeniCevaplar, yeniDiger) : {},
        },
      });
      setBekleyen(false);
    }, OTOMATIK_KAYIT_GECIKMESI_MS);
  };
  useEffect(
    () => () => {
      if (zamanlayici.current) clearTimeout(zamanlayici.current);
    },
    [],
  );

  /**
   * Gönderim: bekleyen otomatik kaydı ATLAMAZ — önce yazar, sonra gönderir.
   *
   * ⚠️ **RET NORMAL BİR YOLDUR, İSTİSNA DEĞİL** (G1'den sonra daha da öyle:
   * zorunlu fotoğrafı/notu eksik kullanıcı buraya sık düşer). Hata
   * YAKALANIR — yakalanmazsa tarayıcıya *unhandled rejection* olarak düşer ve
   * kullanıcı **hiçbir şey görmez**; mesaj zaten `gonder.error`dan ekrana
   * basılıyor (§2.40 — basılınca sessiz kalan düğme bir arızadır).
   */
  const gonderimeGit = async (): Promise<void> => {
    if (!taslak) return;
    if (zamanlayici.current) clearTimeout(zamanlayici.current);
    setBekleyen(false);
    try {
      await kayitGuncelle.mutateAsync({
        id: taslak.id,
        girdi: {
          cevaplar,
          doldurmaTarihi: tarih,
          ekipmanId,
          notlar,
          digerMetinleri: sema ? gonderilecekDigerMetinleri(sema, cevaplar, digerMetinleri) : {},
        },
      });
      await gonder.mutateAsync({ id: taslak.id });
    } catch {
      return; // mesaj `hata` bandında görünür
    }
    // Kap içinde çizildiyse SAYFA DEĞİŞMEZ — kap haber alır (G2.1); ayrı
    // sayfadaysa dönüş tura (S4 — bulgu seçimi orada) ya da kayda.
    if (kap) {
      kap.onGonderildi();
      return;
    }
    void navigate(
      sahaTuruId
        ? `/saha-turlari/${sahaTuruId}`
        : gorevId
          ? `/ekipman/gorevler/${gorevId}`
          : `/formlar/kayitlar/${taslak.id}`,
    );
  };

  // G1 — EKLER SUNUCUDAN OKUNUR (yerel listede biriktirilmez): yükleme
  // `form-submissions` sorgusunu tazeler ve satırlar buradan gelir; ikinci
  // bir ek listesi tutmak, silinen ekin ekranda kalmasına yol açardı.
  const taslakKayit = useFormKayit(taslak?.id ?? null);
  const ekler = taslakKayit.data?.ekler ?? taslak?.ekler ?? [];

  const hata = kayitAc.error ?? kayitGuncelle.error ?? gonder.error ?? ekYukle.error ?? ekSil.error;

  /**
   * G5 — PASİF *"Gönder"* DÜĞMESİNİN SEBEBİ. `null` ise düğme aktiftir.
   * ⚠️ Sebep düğmenin YANINDA durur: altta gri bir açıklama, kullanıcının
   * *"neden basamıyorum"* sorusuna cevap vermiyordu — kendi hatası mı sistem
   * arızası mı ayırt edemiyordu (§2.40).
   */
  const gonderimEngeli: string | null =
    taslak === null
      ? gorevId || kap
        ? 'Taslak açılıyor…'
        : 'Önce işyerini seçin'
      : (digerEksigiMetni(sema ? digerEksikleri(sema, cevaplar, digerMetinleri) : []) ??
        (kayitGuncelle.isPending || bekleyen ? 'Yazdıklarınız kaydediliyor…' : null));

  return (
    <Stack>
      {/* T4 — gömülü formun başlık bloğu (başlık · işyeri · ekipman · tarih)
          GİZLİ: üst kayıt (tur) bunları zaten gösterir. Taslak rozeti kalır. */}
      <Group justify={kap ? 'flex-end' : 'space-between'} align="baseline">
        {!kap && <Title order={3}>{sablon.ad}</Title>}
        {taslak && (
          <Badge
            size="sm"
            color={bekleyen || kayitGuncelle.isPending ? ANLAM.dikkat : ANLAM.olumlu}
            variant="light"
            data-testid="form-doldur-taslak-rozeti"
          >
            {bekleyen
              ? 'Kaydedilmedi'
              : kayitGuncelle.isPending
                ? 'Kaydediliyor…'
                : 'Taslak sunucuda kayıtlı'}
          </Badge>
        )}
      </Group>

      {!kap && (
        <Card withBorder>
          <Group align="flex-end" gap="md" wrap="wrap">
            {gorevId ? (
              // T3 — görev nesnesini BİLİR: işyeri ve ekipman sorulmaz, gösterilir.
              <Stack gap={2} data-testid="form-doldur-gorev-nesnesi">
                <Text size="xs" c="dimmed">
                  Görev
                </Text>
                <Text size="sm">
                  {gorev.data ? `${gorev.data.nesne.ad} · ${gorev.data.nesne.workplaceAd}` : '…'}
                </Text>
              </Stack>
            ) : (
              <>
                <Select
                  w={320}
                  label="İşyeri"
                  description={sahaTuruId ? 'Turun işyeri' : 'Seçilince taslak sunucuda açılır'}
                  data={isyeriSecenekleri}
                  value={workplaceId}
                  disabled={taslak !== null || sahaTuruId !== null}
                  onChange={(value) => {
                    setWorkplaceId(value);
                    setEkipmanId(null);
                  }}
                  data-testid="form-doldur-isyeri"
                />
                {/* F1 — NESNE EKSENİ: ekipman seçilirse kayıt O EKİPMANIN kontrolü
              olur ve gönderim onun açık görevlerini kapatır (§22.6a/G2).
              Boş bırakılırsa nesne İŞYERİNİN KENDİSİDİR (Z2). */}
                <Select
                  w={280}
                  label="İş ekipmanı (opsiyonel)"
                  description="Periyodik kontrol kaydıysa ekipmanı seçin"
                  placeholder={workplaceId ? 'Ekipman seçin' : 'Önce işyeri seçin'}
                  data={ekipmanSecenekleri}
                  value={ekipmanId}
                  clearable
                  disabled={!workplaceId}
                  onChange={(value) => {
                    setEkipmanId(value);
                    yaz(cevaplar, tarih, value);
                  }}
                  data-testid="form-doldur-ekipman"
                />
              </>
            )}
            <TextInput
              w={180}
              label="Tarih"
              type="date"
              value={tarih}
              onChange={(event) => {
                setTarih(event.currentTarget.value);
                yaz(cevaplar, event.currentTarget.value);
              }}
              data-testid="form-doldur-tarih"
            />
            {secilenIsyeri?.kaynak === 'atama' && (
              <Badge
                size="sm"
                color={ANLAM.bilgi}
                variant="light"
                data-testid="form-doldur-atama-rozeti"
              >
                Size verilmiş görev
              </Badge>
            )}
          </Group>
        </Card>
      )}

      {hata && (
        <Alert color={ANLAM.engel} role="alert" data-testid="form-doldur-hata">
          {hata.message}
        </Alert>
      )}

      <QueryStatus queries={mevcutKayitId ? [surum, hedefler, mevcutKayit] : [surum, hedefler]} />
      {sema === null ? (
        <Text size="sm" c="dimmed">
          Şablon sürümü yükleniyor…
        </Text>
      ) : (
        <Card withBorder data-testid="form-doldur-alanlar">
          <FormAlanlari
            sema={sema}
            cevaplar={cevaplar}
            lookupKalemleri={lookup.data ?? []}
            onChange={(yeni, yeniDiger) => {
              setCevaplar(yeni);
              setDigerMetinleri(yeniDiger);
              yaz(yeni, tarih, ekipmanId, notlar, yeniDiger);
            }}
            digerMetinleri={digerMetinleri}
            kanit={{
              ekler,
              notlar,
              kayitAcik: taslak !== null,
              onNot: (alanKodu, metin) => {
                const yeni = { ...notlar, [alanKodu]: metin };
                setNotlar(yeni);
                yaz(cevaplar, tarih, ekipmanId, yeni);
              },
              onFotograf: (alanKodu, dosya) => {
                if (!taslak) return;
                ekYukle.mutate({ submissionId: taslak.id, alanKodu, dosya });
              },
              onFotografSil: (attachmentId) => {
                if (!taslak) return;
                ekSil.mutate({ submissionId: taslak.id, attachmentId });
              },
              onOnizleme: async (attachmentId) => {
                if (!taslak) throw new Error('Kayıt açılmadı');
                return (await ekIndir(taslak.id, attachmentId)).blob;
              },
            }}
          />
        </Card>
      )}

      <Group justify="flex-end" align="center">
        {kap
          ? kap.onVazgec && (
              <Button variant="default" onClick={kap.onVazgec} data-testid="form-kap-vazgec">
                Kapat
              </Button>
            )
          : taslak && (
              <Button
                variant="default"
                component={Link}
                to={`/formlar/kayitlar/${taslak.id}`}
                data-testid="form-taslak-devam"
              >
                Taslağı bırak
              </Button>
            )}
        {/* ⚠️ G5 — SEBEP DÜĞMENİN YANINDA. Eskiden sayfanın altında gri bir
            açıklama vardı ve düğmeye bakan onu görmüyordu. */}
        {gonderimEngeli !== null && (
          <Text size="xs" c="dimmed" data-testid="form-gonder-engeli">
            {gonderimEngeli}
          </Text>
        )}
        <Button
          disabled={gonderimEngeli !== null || gonder.isPending}
          loading={gonder.isPending}
          onClick={() => void gonderimeGit()}
          data-testid="form-gonder"
        >
          Gönder
        </Button>
      </Group>
    </Stack>
  );
}
